
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import SuspiciousUsersPanel from "./security/SuspiciousUsersPanel";
import AbuseStrikesPanel from "./security/AbuseStrikesPanel";
import DeletedUsersPanel from "./security/DeletedUsersPanel";
import BlockedIpsPanel from "./security/BlockedIpsPanel";
import OtpCaptchaPreview from "./security/OtpCaptchaPreview";

const card = {
  background: "rgba(255,255,255,0.92)",
  border: "1px solid rgba(0,0,0,0.12)",
  borderRadius: 16,
  padding: 18,
};

const smallBtn = (primary = false) => ({
  border: "none",
  cursor: "pointer",
  borderRadius: 12,
  padding: "10px 14px",
  fontWeight: 800,
  background: primary ? "#0b5360" : "#f1f1f1",
  color: primary ? "#fff" : "#111",
});

const navBtn = (active = false) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  textDecoration: "none",
  borderRadius: 12,
  padding: "10px 14px",
  fontWeight: 800,
  background: active ? "#0b5360" : "#f6f6f6",
  color: active ? "#fff" : "#111",
  border: active ? "1px solid #0b5360" : "1px solid #dedede",
});

const input = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid #ccc",
  width: 120,
};

export default function AdminSecurityPanel() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [audit, setAudit] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settings, setSettings] = useState({});
  const [activeTab, setActiveTab] = useState("overview");

  async function loadAudit() {
    try {
      setLoading(true);
      setErr("");
      const { data } = await api.get("admin/security/audit");
      setAudit(data || null);
      setSettings(data?.settings || {});
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Failed to load security audit");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAudit();
  }, []);

  const suspiciousUsers = audit?.suspicious_users || [];
  const deletedUsers = audit?.deleted_users || [];
  const blockedIps = audit?.banned_ips || audit?.blocked_ips || [];
  const strikeRows = audit?.strike_rows || audit?.recent_ip_strikes || [];
  const checks = audit?.checks || audit?.audit?.checks || [];

  const stats = useMemo(() => ({
    checksOn: checks.filter((r) => r.status === "ON").length,
    suspiciousCount: suspiciousUsers.length,
    strikeCount: strikeRows.length,
    blockedCount: Number(blockedIps.length || 0),
    deletedCount: deletedUsers.length,
    capturasCount:
      (String(settings?.otp_required || "0") === "1" ? 1 : 0) +
      (String(settings?.captcha_required || "0") === "1" ? 1 : 0),
  }), [checks, suspiciousUsers, strikeRows, blockedIps, deletedUsers, settings]);

  async function saveAllSettings() {
    try {
      setSavingSettings(true);
      await api.post("admin/security/settings", { settings });
      await loadAudit();
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || "Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  }

  return (
    <section className="acctPage">
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <h2 style={{ margin: 0, fontSize: 22 }}>Security audit</h2>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button type="button" style={smallBtn(true)} onClick={loadAudit}>Run audit</button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
          <button type="button" style={navBtn(activeTab === "overview")} onClick={() => setActiveTab("overview")}>
            Overview
          </button>
          <button type="button" style={navBtn(activeTab === "users")} onClick={() => setActiveTab("users")}>
            Suspicious users ({stats.suspiciousCount})
          </button>
          <button type="button" style={navBtn(activeTab === "strikes")} onClick={() => setActiveTab("strikes")}>
            Recent abuse strikes ({stats.strikeCount})
          </button>
          <button type="button" style={navBtn(activeTab === "deleted")} onClick={() => setActiveTab("deleted")}>
            Deleted users ({stats.deletedCount})
          </button>
          <button type="button" style={navBtn(activeTab === "blocked")} onClick={() => setActiveTab("blocked")}>
            Blocked IPs ({stats.blockedCount})
          </button>
          <button type="button" style={navBtn(activeTab === "capturas")} onClick={() => setActiveTab("capturas")}>
            Capturas ({stats.capturasCount})
          </button>
        </div>

        {err ? <p style={{ color: "#cc3344", marginTop: 14 }}>{err}</p> : null}
        {loading ? <p style={{ marginTop: 14 }}>Loading…</p> : null}

        {audit && activeTab === "overview" ? (
          <>
            <div style={statsGrid}>
              <StatBox label="Checks ON" value={stats.checksOn} sub={`${checks.length || 0} total checks`} />
              <StatBox label="Suspicious users" value={stats.suspiciousCount} sub="needs review" tone="warn" />
              <StatBox label="Abuse strikes" value={stats.strikeCount} sub={`${strikeRows.length} IP rows`} tone="warn" />
              <StatBox label="Blocked IPs" value={stats.blockedCount} sub="review list" tone="danger" />
              <StatBox label="Deleted users" value={stats.deletedCount} sub="logged for review" />
            </div>

            <div style={{ background: "#f3ebd7", border: "1px solid #dfc07b", borderRadius: 12, padding: 14, marginTop: 16 }}>
              <strong>Note:</strong> {audit.message || "This reduces obvious bot, curl, and Postman abuse, but no header-based check can stop every spoofed request. Use it together with honeypot, rate limits, OTP/captcha, and IP blocks."}
            </div>
          </>
        ) : null}
      </div>

      {activeTab === "overview" ? (
        <>
          <div style={card}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Check</th>
                  <th style={th}>Status</th>
                  <th style={th}>Note</th>
                  <th style={thRight}>Setting</th>
                </tr>
              </thead>
              <tbody>
                {checks.map((row, index) => {
                  const key = row.key || row.setting_key || row.name || "";
                  const settingType = getSettingType(key, settings[key]);
                  const label = row.label || labelForSetting(key) || key;
                  return (
                    <tr key={row.key || row.label || index}>
                      <td style={td}>{label}</td>
                      <td style={td}>
                        <span style={{ fontWeight: 800, color: row.status === "ON" ? "#126b2f" : "#9b6b00" }}>
                          {row.status}
                        </span>
                      </td>
                      <td style={td}>{row.note}</td>
                      <td style={tdRight}>
                        {settingType === "toggle" ? (
                          <button
                            type="button"
                            style={toggleBtn(String(settings[key] || "0") === "1")}
                            onClick={() =>
                              setSettings((prev) => ({
                                ...prev,
                                [key]: String(prev[key] || "0") === "1" ? "0" : "1",
                              }))
                            }
                          >
                            {String(settings[key] || "0") === "1" ? "ON" : "OFF"}
                          </button>
                        ) : settingType === "number" ? (
                          <input
                            value={settings[key] ?? ""}
                            onChange={(e) =>
                              setSettings((prev) => ({ ...prev, [key]: e.target.value }))
                            }
                            style={input}
                          />
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!checks.length ? (
                  <tr><td colSpan={4} style={td}>No audit checks found.</td></tr>
                ) : null}
              </tbody>
            </table>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <button type="button" style={smallBtn(true)} onClick={saveAllSettings} disabled={savingSettings}>
                {savingSettings ? "Saving…" : "Save settings"}
              </button>
            </div>
          </div>

          <div style={{ ...card, marginTop: 16 }}>
            <h3 style={{ marginTop: 0, fontSize: 20 }}>Extra security settings</h3>
            <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
              {extraSettings(settings, checks).map(([key, value]) => {
                const isBoolean = value === "0" || value === "1";
                return (
                  <label
                    key={key}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      alignItems: "center",
                      borderBottom: "1px solid #eee",
                      paddingBottom: 8,
                    }}
                  >
                    <span style={{ fontWeight: 700 }}>{labelForSetting(key)}</span>
                    {isBoolean ? (
                      <button
                        type="button"
                        style={toggleBtn(String(value) === "1")}
                        onClick={() =>
                          setSettings((prev) => ({
                            ...prev,
                            [key]: String(prev[key] || "0") === "1" ? "0" : "1",
                          }))
                        }
                      >
                        {String(value) === "1" ? "ON" : "OFF"}
                      </button>
                    ) : (
                      <input
                        value={value}
                        onChange={(e) => setSettings((prev) => ({ ...prev, [key]: e.target.value }))}
                        style={input}
                      />
                    )}
                  </label>
                );
              })}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <button type="button" style={smallBtn(true)} onClick={saveAllSettings} disabled={savingSettings}>
                {savingSettings ? "Saving…" : "Save settings"}
              </button>
            </div>
          </div>
        </>
      ) : null}

      {activeTab === "users" ? (
        <div style={card}>
          <SuspiciousUsersPanel rows={suspiciousUsers} onRefresh={loadAudit} />
        </div>
      ) : null}

      {activeTab === "strikes" ? (
        <div style={card}>
          <AbuseStrikesPanel rows={strikeRows} />
        </div>
      ) : null}

      {activeTab === "deleted" ? (
        <div style={card}>
          <DeletedUsersPanel rows={deletedUsers} />
        </div>
      ) : null}

      {activeTab === "blocked" ? (
        <div style={card}>
          <BlockedIpsPanel rows={blockedIps} onRefresh={loadAudit} />
        </div>
      ) : null}

      {activeTab === "capturas" ? (
        <div style={card}>
          <OtpCaptchaPreview
            settings={[
              { key: "otp_required", value: settings?.otp_required || "0" },
              { key: "captcha_required", value: settings?.captcha_required || "0" },
            ]}
          />
        </div>
      ) : null}
    </section>
  );
}

function getSettingType(key, value) {
  const numericKeys = new Set([
    "register_window_seconds",
    "register_max_attempts",
    "abuse_ip_strike_limit",
    "abuse_window_minutes",
    "suspicious_username_score_limit",
  ]);
  if (numericKeys.has(key)) return "number";
  if (value === "0" || value === "1") return "toggle";
  return "none";
}

function extraSettings(settings, checks) {
  const usedKeys = new Set((checks || []).map((x) => x.key || x.setting_key || x.name).filter(Boolean));
  return Object.entries(settings || {}).filter(([key]) => !usedKeys.has(key));
}

function StatBox({ label, value, sub, tone = "default" }) {
  const tones = {
    default: { bg: "#f7f7f7", border: "#e4e4e4" },
    warn: { bg: "#fff6e3", border: "#ead09b" },
    danger: { bg: "#fff0f0", border: "#edc4c4" },
  };
  const c = tones[tone] || tones.default;
  return (
    <div style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 14, padding: 14 }}>
      <div style={{ fontSize: 13, color: "#555", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, color: "#666", marginTop: 6 }}>{sub}</div>
    </div>
  );
}

function labelForSetting(key) {
  const labels = {
    post_only_enabled: "POST-only requests",
    origin_check_enabled: "Origin check",
    xhr_check_enabled: "X-Requested-With check",
    browser_headers_check_enabled: "Browser-like header check",
    browser_like_header_check_enabled: "Browser-like header check",
    referer_check_enabled: "Referer required",
    user_agent_check_enabled: "User-Agent required",
    honeypot_enabled: "Honeypot field",
    rate_limit_enabled: "Rate limiting",
    ip_blocking_enabled: "IP blocking",
    suspicious_name_check_enabled: "Suspicious username detection",
    block_disposable_email_enabled: "Block disposable email",
    otp_required: "OTP required",
    captcha_required: "Captcha required",
    registration_enabled: "Registration enabled",
    register_window_seconds: "Register window seconds",
    register_max_attempts: "Register max attempts",
    auto_reject_suspicious_users: "Auto reject suspicious usernames",
    auto_ban_repeat_abuse_ip: "Auto ban repeat abusive IPs",
    abuse_ip_strike_limit: "IP strike limit",
    abuse_window_minutes: "Abuse window minutes",
    suspicious_username_score_limit: "Suspicious username score limit",
  };
  return labels[key] || key;
}

function toggleBtn(on) {
  return {
    border: "none",
    cursor: "pointer",
    borderRadius: 999,
    padding: "8px 12px",
    fontWeight: 800,
    background: on ? "#0b5360" : "#f1f1f1",
    color: on ? "#fff" : "#111",
    minWidth: 72,
  };
}

const th = { textAlign: "left", padding: "10px 8px", borderBottom: "1px solid #ddd" };
const thRight = { textAlign: "right", padding: "10px 8px", borderBottom: "1px solid #ddd" };
const td = { padding: "10px 8px", borderBottom: "1px solid #eee", verticalAlign: "top" };
const tdRight = { padding: "10px 8px", borderBottom: "1px solid #eee", verticalAlign: "top", textAlign: "right" };
const statsGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginTop: 16 };
